/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  ChangeDetectorRef,
  Component,
  OnInit, ViewChild
} from '@angular/core';

import { Logger                } from '@/util/logger';
import { MatSidenav }            from "@angular/material/sidenav";
import { UserService }           from "@/services/user.service";
import { IUserRow }              from '@/screens/administration/i.user.row';
import {first}                   from "rxjs/operators";
import { PrimeIcons }            from "primeng/api";
import { SortEvent }             from 'primeng/api';
import {fromMySQLDate} from "@/util/util";

@Component({
  standalone: false,
  templateUrl: 'user.list.component.html'
})

/**
 *
 * @class UserListComponent
 *
 * @brief user management page
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2022
 *
 */
export class UserListComponent implements OnInit {

  @ViewChild('adminsidenav')
  public         nav: MatSidenav;

  /** the list of CSV style entries for each user in the system */
  public userList: IUserRow[] = [];

  /** standard constructor */
  constructor(
    private users: UserService,
    private cdRef:  ChangeDetectorRef
  ) {


  }

  /** on page setup */
  ngOnInit() {

    Logger.debug('[UserList] init...');

    /* fetch the user dump... */

    let ul: UserListComponent = this;

    ul.users.dump()
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let rows: IUserRow[] = [];

          if (data.result) {

            rows = data.result;
          }

          Logger.debug(`[UserList] users: `, rows);

          ul.userList = rows;

          /* localize any date/timestamp stuff */

          ul.userList = ul.userList.map((item: IUserRow): IUserRow => {

            item.created_at = fromMySQLDate(item.created_at).toLocaleString().slice(0, 19).replace(',', '');
            item.updated_at = fromMySQLDate(item.updated_at).toLocaleString().slice(0, 19).replace(',', '');

            return item;
          });

          ul.cdRef.detectChanges();
        },

        error: (error: any): void => {

          Logger.error(`[UserList] could not fetch user report: `, error);

        }

      });


  }

  /** expand/collapse the sidebar navigation panel */
  toggleSidebar() {

    this.nav.toggle();
  }

  /** helper to sort special rows like disk usage */
  tableSort(event: SortEvent) {

    let ul: UserListComponent = this;

    event.data.sort((data1, data2) => {

      let value1 = data1[event.field];
      let value2 = data2[event.field];

      /* deal with missing data */

      if (value1 == null && value2 != null) {

        return -1;

      } else if (value1 != null && value2 == null) {

        return 1;

      } else if (value1 == null && value2 == null) {

        return 0;
      }

      if (['id', 'files'].includes(event.field)) {

        /* straight number compare */

        return (parseFloat(value2) - parseFloat(value1)) * event.order;
      }

      if (['userid', 'display_name', 'email'].includes(event.field)) {

        /* straight string compare */

        return (value1.toString().localeCompare(value2.toString())) * event.order;
      }

      if (event.field == 'created_at') {

        /* date compare */

        /* in some browsers the date format may not be 2025-01-05 11:30:10, it may be 01/05/2025 11:30:10 */

        if (/\//.test(value1)) {

          const t1 = value1.split(/[- :/]/);
          const d1 = new Date(Date.UTC(t1[2], t1[0] - 1, t1[1], t1[3], t1[4], t1[5]));

          const t2 = value2.split(/[- :/]/);
          const d2 = new Date(Date.UTC(t2[2], t2[0] - 1, t2[1], t2[3], t2[4], t2[5]));

          return (d2.getTime() - d1.getTime()) * event.order;

        } else {


          const t1 = value1.split(/[- :/]/);
          const d1 = new Date(Date.UTC(t1[0], t1[1] - 1, t1[2], t1[3], t1[4], t1[5]));

          const t2 = value2.split(/[- :]/);
          const d2 = new Date(Date.UTC(t2[0], t2[1] - 1, t2[2], t2[3], t2[4], t2[5]));

          return (d2.getTime() - d1.getTime()) * event.order;
        }
      }

      if (event.field == 'usage') {

        let v1 = parseFloat(value1);
        let v2 = parseFloat(value2);

        if (!isNaN(v1) && isNaN(v2)) {

          return -1;

        } else if (isNaN(v1) && !isNaN(v2)) {

          return 1;

        } else if (isNaN(v1) && isNaN(v2)) {

          return 0;
        }

        /* pretty printed bytes compare */

        if (/kib|k|kb$/.test(value1.toString().toLowerCase())) {

          v1 *= 1024;
        }

        if (/mib|m|mb$/.test(value1.toString().toLowerCase())) {

          v1 *= 1024 * 1024;
        }

        if (/gib|g|gb$/.test(value1.toString().toLowerCase())) {

          v1 *= 1024 * 1024 * 1024;
        }

        if (/tib|t|tb$/.test(value1.toString().toLowerCase())) {

          v1 *= 1024 * 1024 * 1024 * 1024;
        }


        if (/kib|k|kb$/.test(value2.toString().toLowerCase())) {

          v2 *= 1024;
        }

        if (/mib|m|mb$/.test(value2.toString().toLowerCase())) {

          v2 *= 1024 * 1024;
        }

        if (/gib|g|gb$/.test(value2.toString().toLowerCase())) {

          v2 *= 1024 * 1024 * 1024;
        }

        if (/tib|t|tb$/.test(value2.toString().toLowerCase())) {

          v2 *= 1024 * 1024 * 1024 * 1024;
        }

        return (v2 - v1) * event.order;
      }

      /* can not interpret */

      return 0;
    });

    ul.cdRef.detectChanges();
  }

  /** helper to update this item in the user list model */
  updateUser(data: IUserRow) {

    let ul: UserListComponent = this;

    ul.userList = ul.userList.map((item: IUserRow) => {

      item.created_at = fromMySQLDate(item.created_at).toLocaleString().slice(0, 19).replace(',', '');
      item.updated_at = fromMySQLDate(item.updated_at).toLocaleString().slice(0, 19).replace(',', '');

      return item.userid == data.userid ? data : item;
    });

    ul.cdRef.detectChanges();
  }

  /** grant admin rights */
  promoteAdmin(userid: string) {

    Logger.debug(`[UserList] promoting user: `, userid);

    let ul: UserListComponent = this;

    ul.users.promoteUser(userid)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let edited: IUserRow = null;

          if (data.result) {

            edited = data.result;
          }

          Logger.debug(`[UserList] (promote) updated: `, edited);

          ul.updateUser(edited);

        },

        error: (error: any): void => {

          Logger.error(`[UserList] (promote) error: `, error);

        }

      });
  }

  /** take away admin rights */
  demoteAdmin(userid: string) {

    Logger.debug(`[UserList] demoting user: `, userid);

    let ul: UserListComponent = this;

    ul.users.demote(userid)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let edited: IUserRow = null;

          if (data.result) {

            edited = data.result;
          }

          Logger.debug(`[UserList] (demote) updated: `, edited);

          ul.updateUser(edited);

        },

        error: (error: any): void => {

          Logger.error(`[UserList] (demote) error: `, error);

        }

      });
  }

  /** lock/suspend a user */
  unlockUser(userid: string) {

    Logger.debug(`[UserList] unlocking user: `, userid);

    let ul: UserListComponent = this;

    ul.users.unlock(userid)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let edited: IUserRow = null;

          if (data.result) {

            edited = data.result;
          }

          Logger.debug(`[UserList] (unlock) updated: `, edited);

          ul.updateUser(edited);

        },

        error: (error: any): void => {

          Logger.error(`[UserList] (unlock) error: `, error);

        }

      });
  }

  /** unlock/resume a user */
  lockUser(userid: string) {

    Logger.debug(`[UserList] locking user: `, userid);

    let ul: UserListComponent = this;

    ul.users.lock(userid)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let edited: IUserRow = null;

          if (data.result) {

            edited = data.result;
          }

          Logger.debug(`[UserList] (lock) updated: `, edited);

          ul.updateUser(edited);

        },

        error: (error: any): void => {

          Logger.error(`[UserList] (lock) error: `, error);

        }

      });
  }

  /** delete a user */
  deleteUser(userid: string) {

    Logger.debug(`[UserList] deleting user: `, userid);

    let ul: UserListComponent = this;

    ul.users.deleteUser(userid)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let edited: IUserRow = null;

          if (data.result) {

            edited = data.result;
          }

          Logger.debug(`[UserList] (delete) updated: `, edited);

          ul.updateUser(edited);

        },

        error: (error: any): void => {

          Logger.error(`[UserList] (delete) error: `, error);

        }

      });
  }

  /** un-delete a user */
  unDeleteUser(userid: string) {

    Logger.debug(`[UserList] un-deleting user: `, userid);

    let ul: UserListComponent = this;

    ul.users.undeleteUser(userid)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let edited: IUserRow = null;

          if (data.result) {

            edited = data.result;
          }

          Logger.debug(`[UserList] (un-delete) updated: `, edited);

          ul.updateUser(edited);

        },

        error: (error: any): void => {

          Logger.error(`[UserList] (un-delete) error: `, error);

        }

      });
  }


}

